import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, first, map, shareReplay, catchError } from 'rxjs';
import { AiOrderParserItem } from '../../models/ai-order-parser-item';
import { AiOrderParserConfig } from '../../models/ai-order-parser-config';
import { Customer } from '../../models/customer';
import { Order } from '../../models/order';
import { Product } from '../../models/product';
import { CustomerService } from '../customers/customer.service';

/**
 * One coded order-metadata property: the master-data table that validates it, and which of that
 * table's fields carry the code and the display name. Per-tenant configuration, so no table name
 * is ever hard-coded on this side.
 */
export interface AiOrderParserCodedField {
  table: string;
  codeField: string;
  displayField: string;
}

/** Mirrors the backend accept list (AiOrderParserFileTypes). */
const SUPPORTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp'
];
const SUPPORTED_FILE_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.csv', '.png', '.jpg', '.jpeg', '.gif', '.webp'];

/** Extension fallback because browsers often mislabel CSV (text/plain, octet-stream, ms-excel). */
export function isSupportedAiParserFile(file: File): boolean {
  return (
    SUPPORTED_FILE_TYPES.includes(file.type) ||
    SUPPORTED_FILE_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))
  );
}

@Injectable({ providedIn: 'root' })
export class AiOrderParserReviewService {
  private http = inject(HttpClient);
  private customerService = inject(CustomerService);

  private readonly baseUrl = 'api/ai-order-parser/orders';

  /** Cached once per session so the customer picker filters in memory instead of per keystroke. */
  private customers$?: Observable<Customer[]>;

  list(): Observable<AiOrderParserItem[]> {
    // maxAge:-1 bypasses the platform CacheInterceptor: the inbox shows status this page itself
    // mutates (parse/validate/submit/reject), so a cached copy is stale the moment one runs.
    return this.http.get<AiOrderParserItem[]>(this.baseUrl, { headers: { maxAge: '-1' } });
  }

  /** Upload an order file (PDF/image/Excel/CSV) manually; the backend stores it and creates a queue item (AWAITING_CONFIGURATION). */
  upload(file: File): Observable<AiOrderParserItem> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<AiOrderParserItem>(`${this.baseUrl}/upload`, form);
  }

  get(id: string): Observable<AiOrderParserItem> {
    // maxAge:-1 for the same reason as list(): re-opening an item must show the state left by the
    // last parse/validate/submit/reject, not the copy cached when it was first opened.
    return this.http.get<AiOrderParserItem>(`${this.baseUrl}/${id}`, { headers: { maxAge: '-1' } });
  }

  /** Product-ID picker on the review row. Reads the synced Product collection directly, never the
   *  ERP - a search-as-you-type source can't afford one live ERP call per candidate per keystroke. */
  searchLocalProducts(term: string): Observable<Product[]> {
    return this.http.get<Product[]>('api/ai-order-parser/products/search', { params: { term } });
  }

  /** Customer picker on the review page's delivery/billing fields. Filters the cached customer
   *  list by id or name, the way lib-customer-select-dropdown does, but without its
   *  selectCustomer() side effect - reviewing an order must not switch the session's customer. */
  searchCustomers(term: string): Observable<Customer[]> {
    this.customers$ = this.customers$ || this.customerService.getCustomers().pipe(shareReplay(1));
    const parts = term.toLowerCase().split(' ').filter(part => !!part);
    return this.customers$.pipe(
      first(),
      map(customers =>
        customers
          .filter(customer =>
            parts.every(
              part =>
                (customer.id || '').toLowerCase().indexOf(part) >= 0 ||
                (customer.name || '').toLowerCase().indexOf(part) >= 0
            )
          )
          .slice(0, 20)
      )
    );
  }

  /** Which order-metadata properties this installation validates against master data, keyed by GME
   *  property. Empty when the installation has configured none - then every field is free text. */
  getCodedFields(): Observable<{ [property: string]: AiOrderParserCodedField }> {
    return this.http.get<{ [property: string]: AiOrderParserCodedField }>(`${this.baseUrl}/coded-fields`);
  }

  /** Whether this installation maps the order's sender email; any failure means it does not. */
  getSenderEmailEnabled(): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/sender-email-enabled`).pipe(catchError(() => of(false)));
  }

  /** Row description-column name resolution. Reads the synced Product collection directly, never
   *  the ERP - unlike ProductService.getProductsByIdsUnfiltered (json/products-list), which for
   *  Fortnox calls the live ERP once per unique product on every page load. */
  getProductsByIdsLocal(ids: string[]): Observable<Product[]> {
    if (!ids || ids.length === 0) {
      return of([]);
    }
    return this.http.post<Product[]>('api/ai-order-parser/products/by-ids', ids);
  }

  updateOrder(id: string, order: Order): Observable<AiOrderParserItem> {
    return this.http.put<AiOrderParserItem>(`${this.baseUrl}/${id}/order`, order);
  }

  /** Re-run the backend validation pipeline against the item's currently-saved order and refresh its warnings. */
  revalidate(id: string): Observable<AiOrderParserItem> {
    return this.http.post<AiOrderParserItem>(`${this.baseUrl}/${id}/revalidate`, null);
  }

  /**
   * Parse (or re-parse) the item. Both arguments are optional: an order from an unknown sender
   * parses without a saved config, and `config` parses with values the reviewer has not saved
   * yet — it wins over `configName` server-side and is never stored.
   * `manualInput` is the reviewer's free text for this one order; it reaches the model only when
   * `useManualInput` is set. An empty `manualInput` is a deliberate clear and is persisted as such;
   * omitting the field entirely leaves whatever is stored untouched.
   */
  parse(
    id: string,
    configName?: string,
    config?: AiOrderParserConfig,
    manualInput?: string,
    useManualInput?: boolean
  ): Observable<AiOrderParserItem> {
    const body: {
      configName?: string;
      config?: AiOrderParserConfig;
      manualInput?: string;
      useManualInput?: boolean;
    } = {};
    if (configName) {
      body.configName = configName;
    }
    if (config) {
      body.config = config;
    }
    if (manualInput !== undefined) {
      body.manualInput = manualInput;
    }
    if (useManualInput) {
      body.useManualInput = useManualInput;
    }
    return this.http.post<AiOrderParserItem>(
      `${this.baseUrl}/${id}/parse`,
      Object.keys(body).length > 0 ? body : null
    );
  }

  /** Approve the reviewed order and create it in the ERP via the standard order pipeline. */
  /**
   * `overrideInvalidRows` is the reviewer knowingly posting an order that still has invalid rows;
   * the backend honours it only when the installation allows overrides.
   * `confirmOrderMetadataDeviations` is the reviewer taking responsibility for order metadata that
   * differs from the customer's registered values; the backend rejects such an order without it.
   * `confirmUnregisteredAddress` is the reviewer accepting a delivery address that matches none of
   * the customer's registered delivery locations; the backend rejects such an order without it.
   * `confirmCustomerOverride` is the reviewer accepting the configuration's customer over the one
   * the document named; the backend rejects such an order without it.
   * The listing's quick submit passes none of them, so it can never override or confirm.
   */
  submitToErp(
    id: string,
    overrideInvalidRows = false,
    confirmOrderMetadataDeviations = false,
    confirmUnregisteredAddress = false,
    confirmCustomerOverride = false
  ): Observable<AiOrderParserItem> {
    const body: {
      overrideInvalidRows?: boolean;
      confirmOrderMetadataDeviations?: boolean;
      confirmUnregisteredAddress?: boolean;
      confirmCustomerOverride?: boolean;
    } = {};
    if (overrideInvalidRows) {
      body.overrideInvalidRows = true;
    }
    if (confirmOrderMetadataDeviations) {
      body.confirmOrderMetadataDeviations = true;
    }
    if (confirmUnregisteredAddress) {
      body.confirmUnregisteredAddress = true;
    }
    if (confirmCustomerOverride) {
      body.confirmCustomerOverride = true;
    }
    return this.http.post<AiOrderParserItem>(
      `${this.baseUrl}/${id}/submit-to-erp`,
      Object.keys(body).length > 0 ? body : null
    );
  }

  reject(id: string, reason: string): Observable<AiOrderParserItem> {
    return this.http.post<AiOrderParserItem>(`${this.baseUrl}/${id}/reject`, { reason });
  }

  /** Manually resolve an item stuck in SUBMIT_OUTCOME_UNKNOWN after the reviewer checked the ERP. */
  resolveOutcome(id: string, posted: boolean, erpOrderIds?: string[]): Observable<AiOrderParserItem> {
    return this.http.post<AiOrderParserItem>(`${this.baseUrl}/${id}/resolve-outcome`, { posted, erpOrderIds });
  }

  /** Permanently delete an inbox item (and its stored attachment) on the backend. */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  listConfigs(): Observable<AiOrderParserConfig[]> {
    // maxAge:-1 as above: configs are mutated by the parser admin and by direct API writes, so a
    // forever-cached list hides a configuration that exists.
    return this.http.get<AiOrderParserConfig[]>('api/ai-order-parser/configs', { headers: { maxAge: '-1' } });
  }

  fetchAttachmentBlob(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/attachment`, { responseType: 'blob' });
  }
}
